import React, { useEffect, useState, useCallback } from "react";
import { FileText, ChevronDown, AlertCircle, Loader2, Pencil, Check, X, Plus, Tag } from "lucide-react";
import { useFormContext } from "react-hook-form";

import FormInput from "@/app/_module/components/FormInput";
import { Form, FormField } from "@/app/_module/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { Badge } from "@/app/_module/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { getAdminAccess } from "@/app/_module/utils/admin-access";

import { type LeadStatus } from "../constants/form-options";
import { type UpdateLeadSchema } from "../schema/lead-schema";
import NoSaleReasonModal from "./NoSaleReasonModal";
import { useUpdateLeadStatus } from "../hooks/queries";
import { useSimpleStatuses, useGetAgencyAgents, useAvailableStatuses } from "../hooks/queries";
import { Input } from "@/app/_module/components/ui/input";
import { leadsService } from "../services/services";
import { useQueryClient } from "@tanstack/react-query";
import { successToast, errorToast } from "@/app/_module/services/helper";
import getStatusColor from "../constants/get-status-color";
import { useAgentWorkload } from "@/app/(authenticated)/lead-assignment/_modules/hooks/queries";
import { useAssignLead } from "@/app/(authenticated)/lead-assignment/_modules/hooks/useAssignLead";
import { useUnassignLead } from "@/app/(authenticated)/lead-assignment/_modules/hooks/useUnassignLead";

import { FormSection } from "./FormSection";

interface MiscellaneousSectionProps {
  isEditing?: boolean; // Make optional since always editing now
  leadData?: any; // Lead data to access assignedToUserId and assignedByName
  leadId?: string; // Lead ID for status changes
  onStatusChange?: (leadId: string, newStatus: string) => Promise<void>; // Handler to sync status changes
}

export const MiscellaneousSection = function({ isEditing = true, leadData, leadId, onStatusChange }: MiscellaneousSectionProps) {
  const form = useFormContext<UpdateLeadSchema>();
  const { user } = useUserContext();
  const [isAssigning, setIsAssigning] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const [isEditingSource, setIsEditingSource] = useState(false);
  const [editSourceValue, setEditSourceValue] = useState("");
  const [isSavingSource, setIsSavingSource] = useState(false);
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const updateLeadStatusMutation = useUpdateLeadStatus();

  const handleEditNoSaleReason = async (reason: string) => {
    if (!leadId) return;
    await updateLeadStatusMutation.mutateAsync({
      leadId,
      status: "No sale",
      noSaleReason: reason,
    });
  };
  const queryClient = useQueryClient();

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = form;

  const leadStat = watch("leadStat");
  const assigned = watch("assigned");

  // Assignment mutation hooks
  const { mutateAsync: assignLead } = useAssignLead();
  const { mutateAsync: unassignLead } = useUnassignLead();

  // Sync form status when leadData changes (e.g., from header status change)
  useEffect(() => {
    if (leadData?.lead_stat || leadData?.leadStat) {
      const newStatus = leadData.lead_stat || leadData.leadStat;
      if (newStatus !== leadStat) {
        setValue("leadStat", newStatus);
      }
    }
  }, [leadData?.lead_stat, leadData?.leadStat, setValue]);

  // Use shared hook for statuses (cached for 15 minutes)
  const { data: statuses = [], isLoading: loading, isError } = useSimpleStatuses();
  const { data: statusesData, isLoading: loadingStatuses } = useAvailableStatuses();
  const availableStatuses: Array<{ value: string; label: string; color?: string }> = statusesData?.statuses || statuses.map((s: any) => ({ value: s, label: s }));
  const error = isError ? "Failed to load statuses" : null;

  // Get current agency ID from user context
  const currentAgencyId = user?.currentAgency?.agency?.id;

  // Fetch agent workload data - this provides both agent list AND workload info
  const { data: agentWorkloadData, isLoading: isAgentsLoading } = useAgentWorkload();
  const agentsWithWorkload = agentWorkloadData?.agents || [];

  // Use agentsWithWorkload as the main agent list (same source as Lead Assignment page)
  const agencyAgents = agentsWithWorkload;

  // Get current user ID - try multiple paths
  const currentUserId = (user?.user as any)?.id || (user?.user as any)?.userId || (user as any)?.id || (user as any)?.userId;

  // Constants for workload display
  const UNLIMITED_THRESHOLD = 2000000000;

  // Get the assigned user's name from the lead data (not from form schema)
  const assignedToUserId = leadData?.assigned_to_user_id || leadData?.assignedToUserId;
  const assignedByName = leadData?.assigned_by_name || leadData?.assignedByName || leadData?.assignedBy || leadData?.assignedBy?.name;
  const assignedToAgencyName = leadData?.assignedToAgencyName || leadData?.assigned_to_agency_name || leadData?.assignedToAgency?.name;
  const assignedAgent = agencyAgents.find((agent: any) => {
    const agentUserId = agent.id || agent.agentId;
    return agentUserId === assignedToUserId;
  });
  const isAssignedToCurrentUser = assignedToUserId && currentUserId && (assignedToUserId === currentUserId);

  // Check if user is agency owner/admin (can edit assignments)
  const agencyRole = user?.currentAgency?.role;
  const userType = user?.user?.userType;
  const adminAccess = getAdminAccess(userType || 'agent', agencyRole);
  const canEditAssignment = adminAccess.hasAgencyManagement || adminAccess.isAgencyOwner || adminAccess.isAgencyAdmin || adminAccess.isSystemAdmin;
  const isAgent = !canEditAssignment && agencyRole === 'agent';

  // Determine the Select value to show
  // If assigned to current user, show current user's name if in list, otherwise special value
  // If assigned to someone else, find matching agent or use assignedByName
  // Otherwise show "__unassigned"
  const getSelectValue = () => {
    if (!assignedToUserId) return "__unassigned";
    
    if (isAssignedToCurrentUser) {
      // Check if current user is in the agency agents list
      const currentUserAgent = agencyAgents.find((agent: any) =>
        (agent as any).id === currentUserId || agent.agentId === currentUserId || (agent as any).userId === currentUserId
      );
      // If current user is in the list, use their name/email/id so it matches a SelectItem
      // This will show as "Name (You)" in the dropdown
      if (currentUserAgent) {
        return (currentUserAgent as any).agentName || (currentUserAgent as any).name || currentUserAgent.email || (currentUserAgent as any).agentId || (currentUserAgent as any).id;
      }
      // Current user not in list, use special value
      return "__assigned_to_you";
    }
    
    // Find matching agent in dropdown
    if (assignedAgent) {
      return (assignedAgent as any).agentName || (assignedAgent as any).name || assignedAgent.email || (assignedAgent as any).agentId || (assignedAgent as any).id;
    }
    
    // If assigned but agent not in list, check if assignedByName matches any agent
    if (assignedByName) {
      const matchingAgent = agencyAgents.find((agent: any) =>
        (agent as any).name === assignedByName || agent.agentName === assignedByName || agent.email === assignedByName
      );
      if (matchingAgent) {
        return (matchingAgent as any).agentName || (matchingAgent as any).name || matchingAgent.email || (matchingAgent as any).agentId || (matchingAgent as any).id;
      }
      // assignedByName doesn't match any agent, use it directly (will be added as custom option)
      return assignedByName;
    }
    
    // Fallback to assigned field if available
    if (assigned) {
      const matchingAgent = agencyAgents.find((agent: any) =>
        (agent as any).name === assigned || agent.agentName === assigned || agent.email === assigned
      );
      if (matchingAgent) {
        return (matchingAgent as any).agentName || (matchingAgent as any).name || matchingAgent.email || (matchingAgent as any).agentId || (matchingAgent as any).id;
      }
      return assigned;
    }
    
    return "__unassigned";
  };

  const selectValue = getSelectValue();

  // Display name for read-only view (for agents)
  const displayAssignedToForAgent = () => {
    if (!assignedToUserId) {
      return "Unassigned";
    }

    // Get current user's name and email for comparison
    const currentUserName = user?.user?.name || '';
    const currentUserEmail = user?.user?.email || '';

    // Check if assigned to current user - use multiple methods to verify
    // 1. Direct ID match (if IDs are available)
    // 2. Agent name match (if assignedAgent matches current user)
    // 3. Name match (if assigned field contains current user's name)
    // 4. Email match (if assigned field contains current user's email)
    // 5. If assignedByName matches current user's name, it's likely assigned to current user (backend bug where assignedByName is set to agent name instead of owner)
    const isAssignedToMe =
      isAssignedToCurrentUser || // Direct ID match
      (assignedAgent && ((assignedAgent as any).id === currentUserId || assignedAgent.agentId === currentUserId || (assignedAgent as any).userId === currentUserId)) || // Agent list match
      (assigned && currentUserName && assigned.toLowerCase().includes(currentUserName.toLowerCase())) || // Name match
      (assigned && currentUserEmail && assigned.toLowerCase().includes(currentUserEmail.toLowerCase())) || // Email match
      (assignedAgent && ((assignedAgent as any).agentName || (assignedAgent as any).name) && currentUserName && ((assignedAgent as any).agentName || (assignedAgent as any).name).toLowerCase() === currentUserName.toLowerCase()) || // Agent name match
      (assignedAgent && assignedAgent.email && currentUserEmail && assignedAgent.email.toLowerCase() === currentUserEmail.toLowerCase()) || // Agent email match
      // Fallback: If assignedByName matches current user's name, assume it's assigned to current user
      // (This handles the case where backend incorrectly sets assignedByName to agent's name instead of owner's name)
      (assignedByName && currentUserName && assignedByName.toLowerCase() === currentUserName.toLowerCase());

    // Build the agency suffix if available
    const agencySuffix = assignedToAgencyName ? ` (${assignedToAgencyName})` : '';

    // If assigned to current user, show "you by [name] (Agency Name)" (label already says "Assigned To")
    if (isAssignedToMe) {
      // assignedByName should be the owner's name, but if it's the agent's own name, skip it
      const isAssignedByNameSameAsAgent = assignedByName && currentUserName &&
        assignedByName.toLowerCase() === currentUserName.toLowerCase();

      // First try assignedByName from lead data (this should be the owner's name)
      if (assignedByName && assignedByName.trim() && !isAssignedByNameSameAsAgent) {
        return `you by ${assignedByName}${agencySuffix}`;
      }

      // Fallback: use agency name or generic text
      const agencyOwnerName = user?.currentAgency?.agency?.name || "agency owner";

      return `you by ${agencyOwnerName}${agencySuffix}`;
    }

    // Assigned to someone else - show their name with agency
    const agentDisplayName = (assignedAgent as any)?.agentName || (assignedAgent as any)?.name || assignedByName || assigned || "Assigned";
    return `${agentDisplayName}${agencySuffix}`;
  };

  // Display name for read-only view (for owners - fallback)
  const getDisplayAssignedTo = () => {
    if (!assignedToUserId) {
      return "Unassigned";
    }
    const agencySuffix = assignedToAgencyName ? ` (${assignedToAgencyName})` : '';
    if (isAssignedToCurrentUser) {
      return `Assigned to you${agencySuffix}`;
    }
    const agentDisplayName = (assignedAgent as any)?.agentName || (assignedAgent as any)?.name || assignedByName || assigned || "Assigned";
    return `${agentDisplayName}${agencySuffix}`;
  };
  const displayAssignedTo = getDisplayAssignedTo();

  return (
    <>
    <FormSection icon={FileText} title="Lead Management">
      <Form {...form}>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Assigned To
            </div>
            {/* Show editable dropdown for agency owners/admins regardless of edit mode
                so assignment can be changed without entering full edit mode. Agents still
                see the read-only fallback below. */}
            {canEditAssignment ? (
              <DropdownMenu onOpenChange={(o) => { if (o) setAgentSearch(""); }}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={`flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 overflow-hidden ${errors.assigned ? "border-red-500" : ""} ${isAgentsLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                    disabled={isAgentsLoading || agencyAgents.length === 0}
                  >
                    <span className={`truncate min-w-0 flex-1 text-left ${!assignedToUserId && !assignedByName && !assigned ? "text-muted-foreground" : ""}`}>
                      {isAssigning ? "Assigning..." : (assignedToUserId || assigned) ? `${(assignedAgent as any)?.agentName || (assignedAgent as any)?.name || assigned || assignedByName || "Assigned"}${assignedToAgencyName ? ` (${assignedToAgencyName})` : ''}` : "Select agent"}
                    </span>
                    {isAgentsLoading || isAssigning ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-96 p-2" sideOffset={4}>
                  {isAgentsLoading ? (
                    <div className="flex items-center justify-center gap-3 px-4 py-6 text-sm text-muted-foreground">
                      <div className="h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                      <span>Loading agents...</span>
                    </div>
                  ) : agencyAgents.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No agents available
                    </div>
                  ) : (
                    <React.Fragment>
                      <div className="px-3 py-2 border-b border-blue-200 bg-blue-50 mb-1">
                        <div className="flex items-center gap-2 text-xs font-semibold text-blue-700 uppercase tracking-wider">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                          </svg>
                          Select Agent
                        </div>
                      </div>
                      <div className="mb-1 px-1">
                        <input
                          type="text"
                          value={agentSearch}
                          onChange={(e) => setAgentSearch(e.target.value)}
                          // Radix DropdownMenu owns keydown for typeahead —
                          // stop propagation or typing steals focus to items.
                          onKeyDown={(e) => e.stopPropagation()}
                          placeholder="Search agents..."
                          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                      <div className="space-y-0.5 max-h-80 overflow-y-auto">
                        <DropdownMenuItem
                          onClick={async () => {
                            if (!leadId || isAssigning) return;
                            // Only unassign if currently assigned
                            if (assignedToUserId) {
                              try {
                                setIsAssigning(true);
                                await unassignLead({ leadIds: [leadId], context: { leadStatuses: [leadStat || 'New lead'], agentId: assignedToUserId } });
                                setValue("assigned", "");
                              } catch (error) {
                                console.error('Failed to unassign lead:', error);
                              } finally {
                                setIsAssigning(false);
                              }
                            } else {
                              setValue("assigned", "");
                            }
                          }}
                          disabled={isAssigning}
                          className={`cursor-pointer rounded-lg px-3 py-2.5 transition-colors ${(!assignedToUserId && !assigned) ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50"} ${isAssigning ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span className="text-sm font-medium text-gray-700">
                              {isAssigning ? "Unassigning..." : "Unassigned"}
                            </span>
                            {(!assignedToUserId && !assigned) && (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                                Current
                              </Badge>
                            )}
                          </div>
                        </DropdownMenuItem>
                        {agencyAgents.filter((a: any) => {
                          if (!agentSearch.trim()) return true;
                          const q = agentSearch.trim().toLowerCase();
                          return ((a.agentName || a.name || "") + " " + (a.email || "")).toLowerCase().includes(q);
                        }).map((agent: any) => {
                          // Agent workload data structure: { id, agentId, name, agentName, currentLeads, activeLeadsCount, maxLeads, ... }
                          const agentId = agent.id || agent.agentId;
                          const agentName = agent.agentName || agent.name || agent.email;
                          const isCurrentUser = agentId === currentUserId;
                          // Check both server-side assignedToUserId AND form field assigned
                          const isSelected = (assignedToUserId && assignedToUserId === agentId) || (assigned && assigned === agentName);

                          // Workload data is already in the agent object
                          const currentLeads = agent.currentLeads || agent.activeLeadsCount || 0;
                          const maxLeads = agent.maxLeads || 0;
                          const isUnlimited = !maxLeads || maxLeads <= 0 || maxLeads >= UNLIMITED_THRESHOLD;
                          const workloadPercentage = isUnlimited ? 0 : (currentLeads / maxLeads) * 100;

                          // Get agent's actual online/offline status from API
                          const agentStatus = agent.status || 'offline';

                          // Determine status badge based on actual online status
                          const getStatusBadge = () => {
                            switch (agentStatus) {
                              case 'online':
                                return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Online</Badge>;
                              case 'busy':
                                return <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">On Call</Badge>;
                              case 'idle':
                                return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 text-xs">Idle</Badge>;
                              case 'dnd':
                                return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Do Not Disturb</Badge>;
                              case 'offline':
                              default:
                                return <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-xs">Offline</Badge>;
                            }
                          };

                          // Determine lead count color
                          const getWorkloadColor = () => {
                            if (isUnlimited) return "text-gray-600";
                            if (workloadPercentage >= 90) return "text-red-600";
                            if (workloadPercentage >= 75) return "text-yellow-600";
                            return "text-green-600";
                          };

                          return (
                            <DropdownMenuItem
                              key={agentId}
                              onClick={async () => {
                                if (!leadId || isAssigning || isSelected) return;
                                try {
                                  setIsAssigning(true);
                                  await assignLead({
                                    leadIds: [leadId],
                                    agentId: agentId,
                                    assignedAgencyId: currentAgencyId || '',
                                  });
                                  setValue("assigned", agentName);
                                } catch (error) {
                                  console.error('Failed to assign lead:', error);
                                } finally {
                                  setIsAssigning(false);
                                }
                              }}
                              disabled={isAssigning}
                              className={`cursor-pointer rounded-lg px-3 py-2.5 transition-colors ${isSelected ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50"} ${isAssigning ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              <div className="flex flex-col gap-2 w-full">
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <span className="text-sm font-medium text-gray-900 truncate">
                                      {agentName}
                                      {isCurrentUser && <span className="text-gray-500 font-normal"> (You)</span>}
                                    </span>
                                    {workloadPercentage >= 90 && !isUnlimited && <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                                  </div>
                                  {isSelected && (
                                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs flex-shrink-0">
                                      Current
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center justify-between w-full">
                                  <div className="flex items-center gap-2">
                                    {getStatusBadge()}
                                    <span className={`text-xs font-semibold ${getWorkloadColor()}`}>
                                      {currentLeads}/{isUnlimited ? "Unlimited" : maxLeads.toLocaleString()} leads
                                    </span>
                                  </div>
                                  {!isUnlimited && (
                                    <span className="text-xs text-gray-500">
                                      {workloadPercentage.toFixed(0)}% capacity
                                    </span>
                                  )}
                                </div>
                              </div>
                            </DropdownMenuItem>
                          );
                        })}
                      </div>
                    </React.Fragment>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="text-sm text-gray-900 font-medium">
                {isAgent ? displayAssignedToForAgent() : displayAssignedTo}
              </div>
            )}
            {!!errors.assigned && <p className="text-sm text-red-600">{errors.assigned.message}</p>}
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Lead Status
            </div>
            <div className="flex items-start">
              {leadStat ? (
                (() => {
                  const matchedStage = availableStatuses.find((s: any) => s.value === leadStat);
                  const stageColor = matchedStage?.color;
                  const stageLabel = matchedStage?.label || leadStat;
                  const badgeStyle = stageColor
                    ? { backgroundColor: stageColor, color: '#fff', borderColor: stageColor }
                    : undefined;
                  const badgeClass = stageColor ? "" : getStatusColor(leadStat);
                  return (
                    <Badge className={badgeClass} style={badgeStyle}>
                      {stageLabel}
                    </Badge>
                  );
                })()
              ) : (
                <span className="text-sm text-gray-500">No status set</span>
              )}
            </div>
            {leadStat === "No sale" && (
              <div className="mt-1.5 flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Reason:</span>
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                  {leadData?.noSaleReason || "Not set"}
                </span>
                <button
                  onClick={() => setShowEditReasonModal(true)}
                  className="text-gray-400 hover:text-blue-600 transition-colors"
                  title="Edit reason"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </div>
            )}
            {!!errors.leadStat && <p className="text-sm text-red-600">{errors.leadStat.message}</p>}
          </div>

          {/* Lead Source / List Name */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Lead Source
            </div>
            <div className="flex items-center gap-2">
              {isEditingSource ? (
                <div className="flex items-center gap-1.5 w-full">
                  <Input
                    value={editSourceValue}
                    onChange={(e) => setEditSourceValue(e.target.value)}
                    placeholder="Custom Leads"
                    className="h-8 text-sm flex-1"
                    autoFocus
                    onKeyDown={async (e) => {
                      if (e.key === "Enter" && leadId) {
                        e.preventDefault();
                        setIsSavingSource(true);
                        try {
                          await leadsService.updateLead(leadId, { listName: editSourceValue || undefined });
                          await queryClient.invalidateQueries({ queryKey: ["leads"] });
                          setIsEditingSource(false);
                          successToast("Lead source updated");
                        } catch (err) {
                          console.error("Failed to update lead source:", err);
                          errorToast("Failed to update lead source");
                        } finally {
                          setIsSavingSource(false);
                        }
                      }
                      if (e.key === "Escape") {
                        setIsEditingSource(false);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-green-100 text-green-600"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (leadId) {
                        setIsSavingSource(true);
                        try {
                          await leadsService.updateLead(leadId, { listName: editSourceValue || undefined });
                          await queryClient.invalidateQueries({ queryKey: ["leads"] });
                          setIsEditingSource(false);
                          successToast("Lead source updated");
                        } catch (err) {
                          console.error("Failed to update lead source:", err);
                          errorToast("Failed to update lead source");
                        } finally {
                          setIsSavingSource(false);
                        }
                      }
                    }}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-red-100 text-red-500"
                    onClick={() => setIsEditingSource(false)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  {leadData?.listName ? (
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                      {leadData.listName}
                    </Badge>
                  ) : (
                    <span className="text-sm text-gray-500">No source</span>
                  )}
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                    onClick={() => {
                      setEditSourceValue(leadData?.listName || "");
                      setIsEditingSource(true);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Tags */}
          <LeadTags leadId={leadId} tags={leadData?.tags || []} />
        </div>
      </Form>
    </FormSection>

    <NoSaleReasonModal
      isOpen={showEditReasonModal}
      onOpenChange={setShowEditReasonModal}
      onConfirm={handleEditNoSaleReason}
    />
    </>
  );
};

// ── Lead Tags Component ──────────────────────────────────────────

const PRESET_TAGS = [
  { value: "hot_lead", label: "Hot Lead", color: "bg-red-50 text-red-700 border-red-200" },
  { value: "interested", label: "Interested", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "callback", label: "Callback", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "not_home", label: "Not Home", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "referral", label: "Referral", color: "bg-violet-50 text-violet-700 border-violet-200" },
  { value: "follow_up", label: "Follow Up", color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { value: "spanish", label: "Spanish", color: "bg-orange-50 text-orange-700 border-orange-200" },
];

// Automatic status-history tags ("Was: Sold & Approved") appended by the
// leads_tag_status_history DB trigger when a lead leaves a meaningful stage.
const isHistoryTag = (tag: string) => tag.startsWith("Was: ");

function getTagStyle(tag: string) {
  const preset = PRESET_TAGS.find(t => t.value === tag);
  if (preset) return preset;
  if (isHistoryTag(tag)) {
    // History tags keep their exact label ("Was: Do not call") and get a
    // distinct amber "record keeping" look.
    return { value: tag, label: tag, color: "bg-amber-50 text-amber-800 border-amber-200" };
  }
  return { value: tag, label: tag.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()), color: "bg-gray-100 text-gray-700 border-gray-200" };
}

function LeadTags({ leadId, tags }: { leadId?: string; tags: string[] }) {
  const [currentTags, setCurrentTags] = useState<string[]>(tags || []);
  const [showPicker, setShowPicker] = useState(false);
  const [customTag, setCustomTag] = useState("");
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    setCurrentTags(tags || []);
  }, [tags]);

  // DELTA-BASED save: fetch the lead's CURRENT server-side tags, apply the
  // single add/remove to that fresh list, then write. The old approach saved
  // this component's full local copy — which silently WIPED any tag added
  // server-side after mount (the status-history trigger's "Was:" tags, a
  // teammate's edits, DialIQ system tags). Caught live: trigger added
  // "Was: Sold & Approved", next tag edit from a stale box erased it.
  const applyTagDelta = useCallback(async (add: string | null, remove: string | null) => {
    if (!leadId) return;
    setSaving(true);
    try {
      const fresh: any = await leadsService.getLead(leadId);
      // Response nests as data.data (axios interceptor unwraps one layer,
      // backend wraps the lead in {message, data}). Probe known shapes and
      // take the first with a real tags array.
      const candidates = [fresh?.data?.data, fresh?.data?.lead, fresh?.data, fresh];
      const freshTags = candidates.find((c: any) => Array.isArray(c?.tags))?.tags as string[] | undefined;
      if (!freshTags) {
        // FAIL-SAFE: never write tags based on possibly-stale local state.
        // Writing currentTags here is exactly how server-side tags (status
        // history, teammates) got wiped. No fresh state = no write.
        console.error('Tag save aborted: could not read fresh lead tags');
        return;
      }
      let newTags: string[] = freshTags;
      if (remove) newTags = newTags.filter(t => t !== remove);
      if (add && !newTags.includes(add)) newTags = [...newTags, add];
      await leadsService.updateLead(leadId, { tags: newTags } as any);
      setCurrentTags(newTags);
      await queryClient.invalidateQueries({ queryKey: ["leads"] });
    } catch (err) {
      console.error("Failed to update tags:", err);
    } finally {
      setSaving(false);
    }
  }, [leadId, queryClient, currentTags]);

  // History tags don't count toward the 5-tag cap — they're automatic record
  // keeping, and shouldn't block agents from tagging their own things.
  const userTagCount = currentTags.filter(t => !isHistoryTag(t)).length;

  const addTag = useCallback((tag: string) => {
    const normalized = tag.trim().toLowerCase().replace(/\s+/g, "_");
    if (!normalized || currentTags.includes(normalized)) return;
    if (userTagCount >= 5) return; // Max 5 user tags per lead (history tags exempt)
    applyTagDelta(normalized, null);
  }, [currentTags, applyTagDelta, userTagCount]);

  const removeTag = useCallback((tag: string) => {
    applyTagDelta(null, tag);
  }, [applyTagDelta]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
          <Tag className="h-3.5 w-3.5 text-gray-400" />
          Tags
        </div>
        <button
          type="button"
          onClick={() => setShowPicker(!showPicker)}
          className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Current tags */}
      <div className="flex flex-wrap gap-1.5">
        {currentTags.length === 0 && !showPicker && (
          <span className="text-sm text-gray-400">No tags</span>
        )}
        {currentTags.map(tag => {
          const style = getTagStyle(tag);
          return (
            <span
              key={tag}
              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${style.color}`}
            >
              {style.label}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="hover:opacity-60 ml-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
      </div>

      {/* Tag picker */}
      {showPicker && userTagCount >= 5 && (
        <p className="text-[11px] text-gray-400">Maximum 5 tags per lead</p>
      )}
      {showPicker && userTagCount < 5 && (
        <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
          {/* Preset tags */}
          <div className="flex flex-wrap gap-1.5">
            {PRESET_TAGS.filter(t => !currentTags.includes(t.value)).map(tag => (
              <button
                key={tag.value}
                type="button"
                onClick={() => addTag(tag.value)}
                className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-all hover:opacity-80 ${tag.color}`}
              >
                {tag.label}
              </button>
            ))}
          </div>

          {/* Custom tag input */}
          <div className="flex items-center gap-1.5">
            <Input
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              placeholder="Custom tag..."
              className="h-7 text-xs flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addTag(customTag);
                  setCustomTag("");
                }
              }}
            />
            <button
              type="button"
              onClick={() => { addTag(customTag); setCustomTag(""); }}
              className="p-1 rounded hover:bg-primary/10 text-primary"
              disabled={!customTag.trim()}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
